import React from 'react';
import {
    BatteryFull,
    Cube,
    House,
    Recycle,
    Sun,
    Wrench,
} from '@phosphor-icons/react';

type QuestionControlProps = {
    question: any;
    selectedProducts?: { [key: string]: boolean };
    isProductSelector?: boolean;
    readonly?: boolean;
    onResponseChange?: (question: any, value: any) => void;
    onProductToggle?: (product: string) => void;
};

function getQuestionType(question: any): string {
    if (!question?.questionType) return 'button';
    const type = String(question.questionType).trim().toLowerCase();
    if (type === 'input-textarea' || type.includes('textarea')) return 'textarea';
    if (type === 'input-numeric' || type.includes('numeric') || type.includes('number')) return 'numeric';
    if (type.includes('text')) return 'textbox';
    if (type.includes('drop') || type.includes('select')) return 'dropdown';
    if (type.includes('radio')) return 'radio';
    if (type.includes('checkbox')) return 'checkbox';
    if (type.includes('button')) return 'button';
    return 'button';
}

function getOptions(question: any): any[] {
    if (!question) return [];
    const opts = [...(question.answerOptions || question.options || [])];
    return opts.sort((a: any, b: any) => Number(a.displayOrder || 0) - Number(b.displayOrder || 0));
}

function getOptionLabel(opt: any): string {
    if (!opt) return '';
    if (typeof opt === 'string') return opt;
    return opt.answerText || opt.label || opt.value || '';
}

function getOptionValue(opt: any): any {
    if (!opt) return null;
    if (typeof opt === 'string') return opt;
    return opt.value !== undefined ? opt.value : opt.answerText || opt.label;
}

function getResponseValues(response: any): any[] {
    if (response === undefined || response === null || response === '') return [];
    if (Array.isArray(response)) {
        return response.reduce((values: any[], item: any) => values.concat(getResponseValues(item)), []);
    }
    if (typeof response === 'object') {
        if (Array.isArray(response.answers)) {
            return response.answers.reduce((values: any[], answer: any) => values.concat(getResponseValues(answer)), []);
        }
        const value =
            response.responseValue !== undefined && response.responseValue !== null
                ? response.responseValue
                : response.value !== undefined && response.value !== null
                  ? response.value
                  : response.answerText !== undefined && response.answerText !== null
                    ? response.answerText
                    : response.answerOptionId;
        return value !== undefined && value !== null && value !== '' ? [value] : [];
    }
    return [response];
}

function getCurrentValue(question: any, type: string): any {
    if (!question) return null;
    const response =
        question.currentResponse !== undefined && question.currentResponse !== null
            ? question.currentResponse
            : question.answer;
    if (response !== undefined && response !== null) {
        const values = getResponseValues(response);
        if (values.length > 1) return values;
        if (values.length === 1) return values[0];
        return type === 'textarea' ? '' : response;
    }
    return null;
}

function isOptionSelected(question: any, opt: any, selectedProducts?: { [key: string]: boolean }, isProductSelector?: boolean): boolean {
    const val = getOptionValue(opt);
    if (isProductSelector) return !!selectedProducts?.[val];
    const current = getCurrentValue(question, getQuestionType(question));
    if (current === undefined || current === null || current === '') return false;
    const optionValues = [val, opt?.answerText, opt?.label, opt?.answerOptionId].filter(
        (item) => item !== undefined && item !== null && item !== '',
    );
    const matchesOption = (item: any) =>
        getResponseValues(item).some((respVal) =>
            optionValues.some((optionVal) => respVal === optionVal || String(respVal) === String(optionVal)),
        );
    if (question?.isMultiSelect) {
        if (Array.isArray(current)) return current.some((item) => matchesOption(item));
        if (typeof current === 'string') {
            return current
                .split(',')
                .map((s) => s.trim())
                .some((part) => matchesOption(part));
        }
    }
    return matchesOption(current);
}

function getOptionToneClass(question: any, opt: any, selectedProducts?: { [key: string]: boolean }, isProductSelector?: boolean): string {
    if (!isOptionSelected(question, opt, selectedProducts, isProductSelector)) return 'option-unselected';
    const tone = opt?.tone;
    const toneStr = tone ? String(tone.tone || '').toLowerCase() : '';
    const colorStr = tone ? String(tone.color || '').toLowerCase() : '';
    if (toneStr.includes('red') || colorStr === '#ff0000' || tone?.toneId === 2 || tone?.isCritical) {
        return 'option-selected-red';
    }
    if (toneStr.includes('amber') || toneStr.includes('yellow') || colorStr === '#ffbf00' || tone?.toneId === 3) {
        return 'option-selected-amber';
    }
    if (toneStr.includes('green') || colorStr === '#008000' || tone?.toneId === 1) {
        return 'option-selected-green';
    }
    return 'option-selected';
}

function getSelectedStyle(question: any, opt: any, selectedProducts?: { [key: string]: boolean }, isProductSelector?: boolean): React.CSSProperties | undefined {
    if (!isOptionSelected(question, opt, selectedProducts, isProductSelector)) return undefined;
    const toneColor = opt?.tone?.color;
    if (!toneColor) return undefined;
    return { backgroundColor: toneColor, borderColor: toneColor, color: '#ffffff' };
}

function getProductIcon(opt: any) {
    const val = getOptionLabel(opt);
    const props = { size: 16, weight: 'duotone' as const };
    switch (val) {
        case 'Solar':
            return <Sun {...props} />;
        case 'Roof':
            return <House {...props} />;
        case 'Battery':
            return <BatteryFull {...props} />;
        case 'RRR':
            return <Recycle {...props} />;
        case 'R&R':
        case 'RR':
            return <Wrench {...props} />;
        default:
            return <Cube {...props} />;
    }
}

function isQuestionPrefilled(question: any): boolean {
    if (!question) return false;
    const isPrefilled = question.isPrefilled === true || String(question.isPrefilled).toLowerCase() === 'true';
    const prefilledFromQuestionId = question.prefilledFromQuestionId;
    const hasPrefilledSource =
        prefilledFromQuestionId !== undefined &&
        prefilledFromQuestionId !== null &&
        prefilledFromQuestionId !== '' &&
        prefilledFromQuestionId !== 0 &&
        String(prefilledFromQuestionId).trim() !== '0';
    return isPrefilled || hasPrefilledSource;
}

export const QuestionControl: React.FC<QuestionControlProps> = ({
    question,
    selectedProducts = {},
    isProductSelector = false,
    readonly = false,
    onResponseChange,
    onProductToggle,
}) => {
    if (!question) return null;
    const type = getQuestionType(question);
    const options = getOptions(question);
    const current = getCurrentValue(question, type);

    const isRequired = !!(question?.required || question?.isMandatory);
    const isPrefilled = isQuestionPrefilled(question);
    const cannotUnselect = isRequired || isPrefilled;

    const onValueChange = (newValue: any) => {
        if (!question) return;
        question.currentResponse = newValue;
        question.answer = newValue;
        onResponseChange?.(question, newValue);
    };

    const onOptionSelect = (opt: any) => {
        if (readonly) return;
        const val = getOptionValue(opt);
        if (isProductSelector) {
            onProductToggle?.(val);
            return;
        }
        const selected = isOptionSelected(question, opt, selectedProducts, isProductSelector);
        if (question?.isMultiSelect) {
            let currentArr: any[] = [];
            if (Array.isArray(current)) currentArr = [...current];
            else if (typeof current === 'string' && current.trim() !== '') {
                currentArr = current.split(',').map((s) => s.trim());
            }
            if (selected) {
                if (cannotUnselect && currentArr.length === 1) return;
                currentArr = currentArr.filter((item) => {
                    const itemVal = typeof item === 'object' ? (item.value !== undefined ? item.value : item.answerText) : item;
                    return itemVal !== val && itemVal !== opt.answerText && itemVal !== opt.answerOptionId;
                });
                onValueChange(currentArr.length > 0 ? currentArr : undefined);
            } else {
                currentArr.push(val);
                onValueChange(currentArr);
            }
        } else {
            if (selected) {
                if (!cannotUnselect) {
                    onValueChange(undefined);
                }
            } else {
                onValueChange(val);
            }
        }
    };

    const notice = (() => {
        if (!current) return null;
        const selectedOpt = options.find((opt) => isOptionSelected(question, opt, selectedProducts, isProductSelector));
        if (!selectedOpt?.errorMessage || selectedOpt.errorMessage.trim() === '') return null;
        return { text: selectedOpt.errorMessage, type: 'red' as const, color: selectedOpt.tone?.color || null };
    })();

    return (
        <div className="obd-src-question-control">
            {type === 'button' && (
                <div className="obd-src-question-control__options">
                    {isProductSelector
                        ? options.map((opt, idx) => {
                              const selected = isOptionSelected(question, opt, selectedProducts, true);
                              return (
                                  <button
                                      key={idx}
                                      type="button"
                                      className={`obd-src-product-toggle ${selected ? 'is-active' : ''}`}
                                      style={getSelectedStyle(question, opt, selectedProducts, true)}
                                      disabled={readonly}
                                      onClick={() => onOptionSelect(opt)}
                                  >
                                      {getProductIcon(opt)}
                                      {getOptionLabel(opt)}
                                  </button>
                              );
                          })
                        : options.map((opt, idx) => (
                              <button
                                  key={idx}
                                  type="button"
                                  className={`obd-src-option-btn ${getOptionToneClass(question, opt, selectedProducts, false)}`}
                                  style={getSelectedStyle(question, opt, selectedProducts, false)}
                                  disabled={readonly}
                                  onClick={() => onOptionSelect(opt)}
                              >
                                  {getOptionLabel(opt)}
                              </button>
                          ))}
                </div>
            )}

            {notice && (
                <div
                    className={`obd-src-tone-notice ${notice.type === 'red' ? 'is-red' : ''}`}
                    style={
                        notice.color
                            ? {
                                  borderColor: notice.color,
                                  backgroundColor: `${notice.color}15`,
                                  color: notice.color,
                              }
                            : undefined
                    }
                >
                    {notice.text}
                </div>
            )}

            {type === 'textbox' && (
                <input
                    type="text"
                    className="obd-src-input"
                    value={current || ''}
                    disabled={readonly}
                    placeholder="Enter response..."
                    onChange={(e) => onValueChange(e.target.value || undefined)}
                />
            )}

            {type === 'textarea' && (
                <textarea
                    className="obd-src-input obd-src-input--textarea"
                    rows={3}
                    value={current || ''}
                    disabled={readonly}
                    placeholder="Enter response..."
                    onChange={(e) => onValueChange(e.target.value || undefined)}
                />
            )}

            {type === 'numeric' && (
                <input
                    type="number"
                    className="obd-src-input"
                    value={current || ''}
                    disabled={readonly}
                    placeholder="Enter number..."
                    onChange={(e) => onValueChange(e.target.value || undefined)}
                />
            )}

            {type === 'dropdown' && (
                <select
                    className="obd-src-input obd-src-select"
                    value={current || ''}
                    disabled={readonly}
                    onChange={(e) => onValueChange(e.target.value === '' ? undefined : e.target.value)}
                >
                    <option value="" disabled={cannotUnselect}>
                        Select an option
                    </option>
                    {options.map((opt, idx) => (
                        <option key={idx} value={getOptionValue(opt)}>
                            {getOptionLabel(opt)}
                        </option>
                    ))}
                </select>
            )}

            {type === 'radio' && (
                <div className="obd-src-choice-stack">
                    {options.map((opt, idx) => {
                        const selected = isOptionSelected(question, opt, selectedProducts, false);
                        return (
                            <label
                                key={idx}
                                className="obd-src-choice"
                                onClick={(e) => {
                                    if (readonly) return;
                                    if (selected && !cannotUnselect) {
                                        e.preventDefault();
                                        onValueChange(undefined);
                                    }
                                }}
                            >
                                <input
                                    type="radio"
                                    name={`qc_radio_${question?.questionId || 'q'}`}
                                    checked={selected}
                                    disabled={readonly}
                                    onChange={() => {
                                        if (!selected) {
                                            onValueChange(getOptionValue(opt));
                                        }
                                    }}
                                />
                                <span>{getOptionLabel(opt)}</span>
                            </label>
                        );
                    })}
                </div>
            )}

            {type === 'checkbox' && (
                <div className="obd-src-choice-row">
                    {options.map((opt, idx) => (
                        <label key={idx} className="obd-src-choice">
                            <input
                                type="checkbox"
                                checked={isOptionSelected(question, opt, selectedProducts, isProductSelector)}
                                disabled={readonly}
                                onChange={() => {
                                    if (isProductSelector) onProductToggle?.(getOptionValue(opt));
                                    else onOptionSelect(opt);
                                }}
                            />
                            <span>{getOptionLabel(opt)}</span>
                        </label>
                    ))}
                </div>
            )}
        </div>
    );
};
